import { jsPDF } from 'jspdf';
import { formatDate, cellText } from './helpers';
import { EQUIPMENT_TYPE_LABELS } from './constants';
import { drawEquipmentDetailPdf } from './equipmentDisplay';
import { normalizeMaintenanceRecord, normalizeReportPayload } from './reportPayload';

export const REPORT_TYPE_LABELS = {
  full: 'Rapport Complet',
  equipment: 'Liste des Équipements',
  maintenance: 'Historique des Maintenances',
  expiring: 'Équipements Expirant Bientôt',
};

export const maintenanceTypeLabel = (type) => {
  if (type === 'routine') return 'Routine';
  if (type === 'inspection') return 'Inspection';
  if (type === 'repair') return 'Réparation';
  return cellText(type);
};

export const maintenanceStatusLabel = (status) => {
  if (status === 'pending') return 'En attente';
  if (status === 'completed') return 'Terminé';
  if (status === 'cancelled') return 'Annulé';
  if (status === 'scheduled') return 'Planifiée';
  if (status === 'in_progress') return 'En cours';
  return cellText(status);
};

export const equipmentStatusLabel = (status) => {
  if (status === 'operational') return 'Opérationnel';
  if (status === 'expired') return 'Expiré';
  if (status === 'needs_maintenance') return 'Maintenance requise';
  if (status === 'out_of_service') return 'Hors service';
  return cellText(status);
};

export const generateSimpleReportPDF = (reportData, reportType, clientInfo, generatedAt) => {
  const data = normalizeReportPayload(reportData || {});
  const doc = new jsPDF();
  
  const primaryColor = [220, 38, 38];
  const secondaryColor = [107, 114, 128];
  
  let yPos = 20;
  
  doc.setFillColor(254, 242, 242);
  doc.rect(0, 0, 210, 35, 'F');
  
  doc.setTextColor(...primaryColor);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('PFE - Gestion Incendie', 105, 15, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  doc.text('Gestion des Équipements de Sécurité Incendie', 105, 22, { align: 'center' });
  
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.line(15, 30, 195, 30);
  
  yPos = 45;
  
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  
  doc.text(REPORT_TYPE_LABELS[reportType] || 'Rapport', 15, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...secondaryColor);
  doc.text(`Client: ${cellText(clientInfo.name)}`, 15, yPos);
  yPos += 5;
  
  if (clientInfo.company) {
    doc.text(`Société: ${cellText(clientInfo.company)}`, 15, yPos);
    yPos += 5;
  }
  
  const reportDate = generatedAt ? new Date(generatedAt) : new Date();
  doc.text(`Date: ${formatDate(reportDate, 'dd/MM/yyyy HH:mm')}`, 15, yPos);
  yPos += 15;
  
  if (data.equipment && data.equipment.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('Équipements', 15, yPos);
    yPos += 8;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    data.equipment.forEach((eq, index) => {
      yPos = drawEquipmentDetailPdf(doc, eq, index, yPos);
    });
    
    yPos += 3;
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${data.equipment.length} équipement(s)`, 15, yPos);
    yPos += 10;
  }
  
  if (data.maintenance && data.maintenance.length > 0) {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('Maintenances', 15, yPos);
    yPos += 8;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    data.maintenance.forEach((raw, index) => {
      const m = normalizeMaintenanceRecord(raw);
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${cellText(m.serialNumber)}`, 15, yPos);
      yPos += 5;
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Type: ${maintenanceTypeLabel(m.type)}`, 20, yPos);
      yPos += 4;
      doc.text(`Date prévue: ${formatDate(m.scheduledDate, 'dd/MM/yyyy')}`, 20, yPos);
      yPos += 4;
      doc.text(`Date réalisée: ${formatDate(m.completedDate, 'dd/MM/yyyy')}`, 20, yPos);
      yPos += 4;
      doc.text(`Statut: ${maintenanceStatusLabel(m.status)}`, 20, yPos);
      yPos += 4;
      doc.text(`Technicien: ${cellText(m.technicianName)}`, 20, yPos);
      yPos += 4;
      if (m.notes) {
        const noteLines = doc.splitTextToSize(`Notes: ${cellText(m.notes)}`, 170);
        doc.text(noteLines, 20, yPos);
        yPos += noteLines.length * 4;
      }
      yPos += 6;
    });
    
    yPos += 3;
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${data.maintenance.length} maintenance(s)`, 15, yPos);
    yPos += 10;
  }

  const showExpiring = reportType === 'expiring' || reportType === 'full';
  if (showExpiring) {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('Équipements Expirant Bientôt', 15, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    const expiringItems = data.expiring || [];
    if (expiringItems.length === 0) {
      doc.text('Aucun équipement expirant dans les 10 prochains jours.', 15, yPos);
      yPos += 8;
    } else {
      expiringItems.forEach((eq, index) => {
        yPos = drawEquipmentDetailPdf(doc, eq, index, yPos);
      });

      yPos += 3;
      doc.setFont('helvetica', 'bold');
      doc.text(`Total: ${expiringItems.length} équipement(s) expirant bientôt`, 15, yPos);
    }
  }
  
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...secondaryColor);
    doc.text(
      `Page ${i} sur ${pageCount}`,
      105,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
    doc.text(
      '© 2025 PETROFEU ÉQUIPEMENTS - Tous droits réservés',
      105,
      doc.internal.pageSize.height - 5,
      { align: 'center' }
    );
  }
  
  return doc;
};

export const downloadSimpleReportPDF = (reportData, reportType, clientInfo, generatedAt) => {
  try {
    const doc = generateSimpleReportPDF(reportData, reportType, clientInfo, generatedAt);
    const safeName = cellText(clientInfo.name).replace(/\s+/g, '_');
    const fileName = `rapport_${reportType}_${safeName}_${Date.now()}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/** Opens the same jsPDF document in a hidden iframe and triggers the browser print dialog. */
export const printSimpleReportPDF = (reportData, reportType, clientInfo, generatedAt) => {
  const doc = generateSimpleReportPDF(reportData, reportType, clientInfo, generatedAt);
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Impression du rapport');
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
  iframe.src = url;

  const cleanup = () => {
    URL.revokeObjectURL(url);
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  iframe.onload = () => {
    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      throw new Error('Impossible d’ouvrir le PDF pour l’impression');
    }
    win.focus();
    win.print();
    win.onafterprint = cleanup;
    setTimeout(cleanup, 60_000);
  };

  document.body.appendChild(iframe);
};
